// pages/MemoryPage.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import memoryService from '../../services/memoryService';

const formatTime = (s) => {
  if (!isFinite(s) || s < 0) return '00:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m.toString().padStart(2, '0')}:${sec
    .toString()
    .padStart(2, '0')}`;
};

const pad = (n) => String(n).padStart(2, '0');

/* ── Four-tile countdown ── */
const Countdown = ({ targetDate, onComplete }) => {
  const [timeLeft, setTimeLeft] = useState(() =>
    Math.max(0, targetDate.getTime() - Date.now())
  );

  useEffect(() => {
    const tick = () => {
      const remaining = Math.max(0, targetDate.getTime() - Date.now());
      setTimeLeft(remaining);
      if (remaining <= 0) onComplete?.();
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetDate, onComplete]);

  const totalSeconds = Math.floor(timeLeft / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const tiles = [
    { value: days, label: 'DAYS' },
    { value: hours, label: 'HOURS' },
    { value: minutes, label: 'MINUTES' },
    { value: seconds, label: 'SECONDS' }
  ];

  return (
    <div className="flex items-start justify-center gap-2 sm:gap-4 mb-8">
      {tiles.map((t, i) => (
        <React.Fragment key={t.label}>
          <div className="flex flex-col items-center">
            <div className="w-[70px] sm:w-[90px] h-[70px] sm:h-[90px] bg-[#F7F5EF] flex flex-col items-center justify-center">
              <span className="text-3xl sm:text-4xl font-bold text-[#1C2A46] tabular-nums">
                {pad(t.value)}
              </span>
              <span className="mt-1 text-[10px] sm:text-xs font-normal tracking-widest text-[#6B635D]">
                {t.label}
              </span>
            </div>
          </div>

          {i < tiles.length - 1 && (
            <span className="text-2xl sm:text-3xl font-bold text-[#1C2A46] pt-4 sm:pt-5">
              :
            </span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

/* ── Audio player ── */
const AudioPlayer = ({ audioUrl }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  }, [audioUrl]);

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) el.play().catch((err) => console.error(err));
    else el.pause();
  };

  const handleSeek = (e) => {
    const el = audioRef.current;
    if (!el || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(
      0,
      Math.min(1, (e.clientX - rect.left) / rect.width)
    );
    el.currentTime = ratio * duration;
  };

  const progressRatio = duration > 0 ? currentTime / duration : 0;

  return (
    <div className="mt-8">
      <audio
        ref={audioRef}
        src={audioUrl}
        preload="metadata"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
      />

      <div className="bg-[#F5F5F5] rounded-sm px-4 py-4 flex items-center gap-4">
        <button
          type="button"
          onClick={togglePlay}
          className="w-12 h-12 rounded-full bg-[#1C2A46] flex items-center justify-center flex-shrink-0 hover:bg-[#2a3a5c] transition-colors"
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? (
            <svg
              className="w-5 h-5 text-white"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
            </svg>
          ) : (
            <svg
              className="w-5 h-5 text-white ml-0.5"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M8 5v14l11-7z" />
            </svg>
          )}
        </button>

        <div className="text-sm sm:text-base text-[#1C2A46] font-mono flex-shrink-0 tabular-nums">
          {formatTime(currentTime)} / {formatTime(duration)}
        </div>

        <div
          className="flex-1 flex items-center gap-[2px] h-12 cursor-pointer select-none"
          onClick={handleSeek}
          role="slider"
          aria-label="Audio position"
        >
          {Array.from({ length: 48 }).map((_, i) => {
            const barRatio = i / 48;
            const isPlayed = barRatio <= progressRatio;
            const barHeight = 6 + ((i * 7) % 34);
            return (
              <div
                key={i}
                className={`w-[2px] rounded-full ${
                  isPlayed ? 'bg-[#55000C]' : 'bg-[#B0A8A0]'
                }`}
                style={{ height: `${barHeight}px` }}
              />
            );
          })}
        </div>

        <div className="relative w-2 h-2 rounded-full bg-[#55000C] flex-shrink-0 pointer-events-none" />
      </div>
    </div>
  );
};

/* ── Locked-state Safari card ── */
const LockedPreviewCard = ({ pageSlug, title, media = [] }) => (
  <div className="w-full max-w-3xl mt-10">
    <div className="rounded-3xl overflow-hidden shadow-xl border-[8px] border-[#D8D8D8] bg-[#D9D9D9]">
      {/* Safari chrome */}
      <div className="bg-gradient-to-b from-[#EDEDED] to-[#E1E1E1] px-4 py-2.5 flex items-center gap-3 border-b border-[#D0D0D0]">
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className="w-3 h-3 rounded-full bg-[#FF5F57]" />
          <span className="w-3 h-3 rounded-full bg-[#FEBC2E]" />
          <span className="w-3 h-3 rounded-full bg-[#28C840]" />
        </div>

        <div className="flex-1 flex justify-center">
          <div className="flex items-center gap-2 bg-white border border-[#D0D0D0] rounded-md px-3 py-1.5 w-full max-w-md">
            <svg
              className="w-3.5 h-3.5 text-[#6B635D] flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth="2"
            >
              <rect x="5" y="11" width="14" height="10" rx="2" />
              <path d="M8 11V8a4 4 0 018 0v3" />
            </svg>
            <span className="text-xs text-[#6B635D] truncate">
              www.kapta.com/memory/{pageSlug || ''}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0 text-[#6B635D]">
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 4v16m8-8H4"
            />
          </svg>
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth="2"
          >
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
            <rect x="14" y="14" width="7" height="7" rx="1" />
          </svg>
        </div>
      </div>

      {/* Preview body */}
      <div className="relative bg-white h-[280px] sm:h-[320px] overflow-hidden">
        <div className="absolute inset-0 flex">
          {media.length > 0 ? (
            media.slice(0, 4).map((item, i) => (
              <div
                key={i}
                className="flex-1 h-full overflow-hidden"
                style={{ flexBasis: `${100 / Math.min(media.length, 4)}%` }}
              >
                {item.type === 'video' ? (
                  <video
                    src={item.url}
                    className="w-full h-full object-cover"
                    muted
                    playsInline
                  />
                ) : (
                  <img
                    src={item.url}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
            ))
          ) : (
            <div className="w-full h-full bg-[#F5F5F5]" />
          )}
        </div>

        {/* Frosted glass layer */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundColor: 'rgba(252, 247, 238, 0.18)',
            backdropFilter: 'blur(8px) saturate(1.2) brightness(1.05)',
            WebkitBackdropFilter: 'blur(8px) saturate(1.2) brightness(1.05)'
          }}
        />

        {/* Glass pill */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className="px-6 sm:px-10 py-5 sm:py-7 mx-4 max-w-lg rounded-2xl"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.55)',
              backdropFilter: 'blur(16px) saturate(1.2)',
              WebkitBackdropFilter: 'blur(16px) saturate(1.2)',
              border: '1px solid rgba(255, 255, 255, 0.7)',
              boxShadow:
                '0 8px 32px rgba(28, 42, 70, 0.08), inset 0 1px 0 rgba(255,255,255,0.6)'
            }}
          >
            <div className="flex flex-col items-center text-center gap-2">
              <svg
                className="w-6 h-6 text-[#55000C]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth="2"
              >
                <rect x="3" y="5" width="18" height="16" rx="2" />
                <path d="M3 10h18M8 3v4M16 3v4" strokeLinecap="round" />
              </svg>
              <h3 className="text-lg md:text-xl font-medium text-[#1C2A46] leading-tight">
                {title || 'Your Memory'}
              </h3>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

/* ── Skeleton loading state — mirrors the unlocked layout ── */
const MemorySkeleton = () => (
  <div className="min-h-screen bg-[#FCF7EE] flex flex-col items-center px-4 py-12 sm:py-16 animate-pulse">
    <div className="w-full max-w-3xl">
      {/* Title */}
      <div className="flex flex-col items-center mb-3">
        <div className="h-8 sm:h-10 md:h-12 w-3/4 max-w-md bg-[#E7E1D6] rounded" />
        <div className="h-8 sm:h-10 md:h-12 w-1/2 max-w-xs bg-[#E7E1D6] rounded mt-2" />
      </div>

      {/* Subtitle line */}
      <div className="flex items-center justify-center gap-3 mb-10">
        <div className="h-4 w-40 bg-[#E7E1D6] rounded" />
        <div className="h-1.5 w-1.5 rounded-full bg-[#E7E1D6]" />
        <div className="h-4 w-52 bg-[#E7E1D6] rounded" />
      </div>

      {/* Media strip */}
      <div className="w-full mb-10">
        <div className="rounded-2xl overflow-hidden border-[6px] sm:border-[8px] border-[#E4E4E4] bg-[#D9D9D9]">
          <div className="flex h-[280px] sm:h-[340px] md:h-[380px]">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="flex-1 h-full bg-[#E7E1D6] border-r border-[#D9D9D9] last:border-r-0"
              />
            ))}
          </div>
        </div>
      </div>

      {/* Description lines */}
      <div className="space-y-3 mb-6">
        <div className="h-4 w-full bg-[#E7E1D6] rounded" />
        <div className="h-4 w-full bg-[#E7E1D6] rounded" />
        <div className="h-4 w-11/12 mx-auto bg-[#E7E1D6] rounded" />
        <div className="h-4 w-9/12 mx-auto bg-[#E7E1D6] rounded" />
      </div>

      {/* Audio player bar */}
      <div className="mt-8">
        <div className="bg-[#F5F5F5] rounded-sm px-4 py-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-[#E7E1D6] flex-shrink-0" />
          <div className="h-4 w-24 bg-[#E7E1D6] rounded flex-shrink-0" />
          <div className="flex-1 h-8 bg-[#E7E1D6] rounded" />
          <div className="w-2 h-2 rounded-full bg-[#E7E1D6] flex-shrink-0" />
        </div>
      </div>
    </div>
  </div>
);

/* ── Main page ── */
const MemoryPage = () => {
  const { pageSlug } = useParams();

  const [memory, setMemory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setIsLoading(true);
      setError('');
      try {
        const res = await memoryService.getMemoryBySlug(pageSlug);
        if (cancelled) return;
        setMemory(res?.data || null);
      } catch (err) {
        if (cancelled) return;
        setError(
          err?.response?.data?.message ||
            err?.message ||
            'This memory could not be found.'
        );
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pageSlug]);

  const revealDate = memory?.revealDate ? new Date(memory.revealDate) : null;
  const hasReveal = revealDate && !isNaN(revealDate.getTime());
  const hasArrived = !hasReveal || revealDate.getTime() <= Date.now();

  useEffect(() => {
    if (!memory) return;
    setUnlocked(hasArrived);
  }, [memory, hasArrived]);

  const revealDateLabel = hasReveal
    ? revealDate.toLocaleDateString('en-US', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    : '';

  const audioUrl = memory?.audioFile?.url || memory?.audioUrl || '';

  /* ────────── SKELETON (loading) ────────── */
  if (isLoading) {
    return <MemorySkeleton />;
  }

  /* ────────── LOCKED STATE ────────── */
  if (!error && memory && !unlocked && hasReveal) {
    return (
      <div className="min-h-screen bg-[#FCF7EE] flex flex-col items-center px-4 py-12 sm:py-16">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#1C2A46] text-center leading-tight mb-3 max-w-2xl">
          Something special has
          <br />
          been kept for you
        </h1>

        <p className="text-sm sm:text-base text-[#6B635D] text-center max-w-md mb-10">
          This memory isn&apos;t ready to be opened just yet.
          <br />
          Come back when the moment arrives.
        </p>

        <Countdown
          targetDate={revealDate}
          onComplete={() => setUnlocked(true)}
        />

        <div className="flex items-center gap-2 mb-10">
          <svg
            className="w-5 h-5 text-[#000000]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth="2"
          >
            <rect x="3" y="5" width="18" height="16" rx="2" />
            <path d="M3 10h18M8 3v4M16 3v4" strokeLinecap="round" />
          </svg>
          <span className="text-sm font-medium sm:text-base text-[#6B635D]">
            Unlocks on the{' '}
            <span className="text-[#55000C] font-medium">
              {revealDateLabel}
            </span>
          </span>
        </div>

        <LockedPreviewCard
          pageSlug={pageSlug}
          title={memory.memoryTitle}
          media={memory.media}
        />

        <style>{`
          .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
        `}</style>
      </div>
    );
  }

  /* ────────── ERROR ────────── */
  if (error || !memory) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-3xl">
          <div className="p-8 min-h-[600px] flex flex-col items-center justify-center text-center">
            <p className="text-lg font-medium text-[#1C2A46] mb-2">
              Memory not found
            </p>
            <p className="text-sm text-[#6B635D] mb-6">
              {error || 'This memory could not be found.'}
            </p>
            <Link
              to="/"
              className="text-sm text-[#55000C] underline hover:text-[#6a0a17]"
            >
              Back to home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  /* ────────── UNLOCKED STATE ────────── */
  return (
    <div className="min-h-screen bg-[#FCF7EE] flex flex-col items-center px-4 py-12 sm:py-16">
      <div className="w-full max-w-3xl">
        {/* Title */}
        {memory.memoryTitle && (
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#1C2A46] text-center leading-tight mb-3">
            {memory.memoryTitle}
          </h1>
        )}

        {/* Subtitle line */}
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-sm sm:text-base text-[#6B635D] mb-10">
          <span className="font-normal">From someone who loves you</span>

          <span className="text-[#6B635D] text-lg leading-none">•</span>

          <span className="inline-flex items-center gap-2">
            <svg
              className="w-4 h-4 sm:w-5 sm:h-5 text-[#55000C]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth="2"
            >
              <rect x="3" y="5" width="18" height="16" rx="2" />
              <path d="M3 10h18M8 3v4M16 3v4" strokeLinecap="round" />
            </svg>
            <span>
              Unlocks on the{' '}
              <span className="text-[#55000C] font-medium">
                {revealDateLabel}
              </span>
            </span>
          </span>
        </div>

        {/* Media strip */}
        {memory.media?.length > 0 && (
          <div className="w-full mb-10">
            <div className="rounded-2xl overflow-hidden border-[6px] sm:border-[8px] border-[#E4E4E4] bg-[#D9D9D9]">
              <div className="flex h-[280px] sm:h-[340px] md:h-[380px]">
                {memory.media.slice(0, 4).map((item, i) => (
                  <div
                    key={i}
                    className="flex-1 h-full overflow-hidden"
                    style={{
                      flexBasis: `${100 / Math.min(memory.media.length, 4)}%`
                    }}
                  >
                    {item.type === 'video' ? (
                      <video
                        src={item.url}
                        className="w-full h-full object-cover"
                        controls
                        playsInline
                      />
                    ) : (
                      <img
                        src={item.url}
                        alt={item.name || `Media ${i + 1}`}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Description */}
        {memory.description && (
          <p className="text-[#6B635D] text-center text-sm sm:text-base leading-relaxed whitespace-pre-wrap mb-4">
            {memory.description}
          </p>
        )}

        {/* Audio player */}
        {audioUrl && (
          <div className="w-full">
            <AudioPlayer audioUrl={audioUrl} />
          </div>
        )}
      </div>

      <style>{`
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default MemoryPage;